<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        md="4"
      >
        <v-text-field
          v-model="enderecoDataLocale.cep"
          v-mask="'#####-###'"
          dense
          outlined
          label="Cep"
          hide-details="auto"
          validate-on-blur
          :filled="filled"
          :readonly="readonly"
          :disabled="disabled"
          :rules="cepRules"
          @change="handleCepChange"
        />
      </v-col>

      <v-col
        cols="12"
        md="8"
      >
        <v-text-field
          v-model.trim="enderecoDataLocale.rua"
          dense
          outlined
          label="Rua"
          hide-details="auto"
          validate-on-blur
          :loading="isGetDadosCorreiosLoading"
          :filled="isRuaFilled"
          :readonly="readonly"
          :disabled="isRuaDisabled"
          :rules="ruaRules"
          @change="v => $emit('update:rua', v)"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        md="4"
      >
        <v-text-field
          v-model.trim="enderecoDataLocale.numero"
          dense
          outlined
          label="Número"
          validate-on-blur
          hide-details="auto"
          :filled="filled"
          :readonly="readonly"
          :disabled="disabled"
          :rules="numeroRules"
          @change="v => $emit('update:numero', v)"
        />
      </v-col>

      <v-col
        cols="12"
        md="5"
      >
        <v-text-field
          v-model.trim="enderecoDataLocale.complemento"
          dense
          outlined
          label="Complemento"
          hide-details="auto"
          validate-on-blur
          :filled="filled"
          :readonly="readonly"
          :disabled="disabled"
          @change="v => $emit('update:complemento', v)"
        />
      </v-col>

      <v-col
        cols="12"
        md="3"
      >
        <v-text-field
          v-model.trim="enderecoDataLocale.estado"
          dense
          outlined
          label="Estado"
          filled
          disabled
          hide-details="auto"
          validate-on-blur
          :readonly="readonly"
          :loading="isGetDadosCorreiosLoading"
          :rules="estadoRules"
          @change="v => $emit('update:estado', v)"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="12"
        md="6"
      >
        <v-text-field
          v-model="enderecoDataLocale.bairro"
          dense
          outlined
          label="Bairro"
          hide-details="auto"
          validate-on-blur
          :loading="isGetDadosCorreiosLoading"
          :filled="isBairroFilled"
          :readonly="readonly"
          :disabled="isBairroDisabled"
          :rules="bairroRules"
          @change="v => $emit('update:bairro', v)"
        />
      </v-col>

      <v-col
        cols="12"
        md="6"
      >
        <v-text-field
          v-model="enderecoDataLocale.cidade"
          dense
          outlined
          label="Cidade"
          filled
          disabled
          hide-details="auto"
          validate-on-blur
          :readonly="readonly"
          :loading="isGetDadosCorreiosLoading"
          :rules="cidadeRules"
          @change="v => $emit('update:cidade', v)"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model.trim="enderecoDataLocale.pontoDeReferencia"
          dense
          outlined
          label="Ponto de referência"
          hide-details="auto"
          validate-on-blur
          :filled="filled"
          :readonly="readonly"
          :disabled="disabled"
          @change="v => $emit('update:ponto-de-referencia', v)"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { computed, watch } from '@vue/composition-api'
import EnderecoModel from '@/models/components/endereco/Endereco'
import useDadosCorreios from '@/composables/useDadosCorreios'
import { required } from '@/@core/utils/validation'

export default {
  emits: ['update:cep', 'update:rua', 'update:numero', 'update:complemento', 'update:estado', 'update:bairro', 'update:cidade', 'update:ponto-de-referencia'],

  props: {
    enderecoData: {
      type: Object,
      default: null,
    },
    filled: Boolean,
    readonly: Boolean,
    disabled: Boolean,
    required: Boolean,
  },

  setup(props, { emit }) {
    const {
      enderecoData: enderecoDataLocale,
      isGetDadosCorreiosLoading,
      bairroFoiPreenchidoPelaApiDosCorreios,
      ruaFoiPreenchidaPelaApiDosCorreios,
      cepFoiPreenchidoCorretamente,
      handleCepChange,
    } = useDadosCorreios(emit)

    watch(
      () => props.enderecoData,
      v => {
        if (v) {
          enderecoDataLocale.value = v
        } else {
          enderecoDataLocale.value = new EnderecoModel()
        }
      },
      { immediate: true, deep: true },
    )

    const isRuaFilled = computed(() => props.filled || ruaFoiPreenchidaPelaApiDosCorreios.value)
    const isRuaDisabled = computed(() => props.disabled || ruaFoiPreenchidaPelaApiDosCorreios.value)

    const isBairroFilled = computed(() => props.filled || bairroFoiPreenchidoPelaApiDosCorreios.value)
    const isBairroDisabled = computed(() => props.disabled || bairroFoiPreenchidoPelaApiDosCorreios.value)

    const cepRules = computed(() => (props.required ? [required] : []))
    const ruaRules = computed(() => (props.required ? [required] : []))
    const numeroRules = computed(() => {
      const rules = [v => (cepFoiPreenchidoCorretamente ? (!!v && v.length <= 10) || 'Máximo de 10 caracteres' : true)]

      if (props.required) {
        rules.push(required)
      }

      return rules
    })
    const estadoRules = computed(() => (props.required ? [required] : []))
    const bairroRules = computed(() => (props.required ? [required] : []))
    const cidadeRules = computed(() => (props.required ? [required] : []))

    return {
      enderecoDataLocale,
      isGetDadosCorreiosLoading,
      ruaFoiPreenchidaPelaApiDosCorreios,
      bairroFoiPreenchidoPelaApiDosCorreios,
      handleCepChange,

      isRuaFilled,
      isRuaDisabled,

      isBairroFilled,
      isBairroDisabled,

      cepRules,
      ruaRules,
      numeroRules,
      bairroRules,
      estadoRules,
      cidadeRules,
    }
  },
}
</script>
